import React from 'react';
import SEO from '../components/SEO';

const FullStackJava: React.FC = () => {
  return (
    <>
      <SEO 
        title="Full Stack Java Developer Program | Complete Java Training with Placement - SBRIT Solution"
        description="Master Full Stack Java development with Spring Boot, React, Microservices, and cloud technologies. 6-month comprehensive program with live projects and 100% placement guarantee."
        keywords="full stack java developer, java full stack course, spring boot training, java microservices, full stack java placement program, java developer certification"
        url="https://sbritsolution.in/courses/full-stack-java"
        type="article"
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-orange-600 to-red-700 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Full Stack Java Developer Program
              </h1>
              <p className="text-xl md:text-2xl mb-8 leading-relaxed">
                Master Enterprise Java Development | Spring Boot | Microservices | React | Cloud Deployment
              </p>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <button className="bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                  Enroll Now - ₹25,999
                </button>
                <button className="border-2 border-white hover:bg-white hover:text-orange-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                  Download Curriculum
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Program Highlights */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Why Choose Our Full Stack Java Program?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🚀</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Enterprise-Ready Skills</h3>
                <p className="text-gray-600">Learn industry-standard Java technologies used by Fortune 500 companies.</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">💼</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">100% Placement Guarantee</h3>
                <p className="text-gray-600">Guaranteed job placement with our extensive industry partnerships.</p>
              </div>
              <div className="text-center p-6 rounded-lg shadow-lg">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🏗️</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">Real-World Projects</h3>
                <p className="text-gray-600">Build production-ready applications with modern architecture patterns.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Comprehensive Curriculum */}
        <div className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Complete Full Stack Java Curriculum</h2>
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-orange-600">Backend Technologies</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Core Java 17 & Advanced Java Concepts</li>
                    <li>• Spring Framework & Spring Boot</li>
                    <li>• Spring Security & JWT Authentication</li>
                    <li>• Spring Data JPA & Hibernate</li>
                    <li>• RESTful Web Services & API Design</li>
                    <li>• Microservices Architecture</li>
                    <li>• Apache Kafka & Message Queues</li>
                    <li>• Database Design (MySQL, PostgreSQL)</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4 text-blue-600">Frontend & DevOps</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• React.js & Modern JavaScript (ES6+)</li>
                    <li>• HTML5, CSS3 & Responsive Design</li>
                    <li>• State Management (Redux/Context API)</li>
                    <li>• Docker & Containerization</li>
                    <li>• AWS Cloud Services (EC2, S3, RDS)</li>
                    <li>• CI/CD Pipelines with Jenkins</li>
                    <li>• Git Version Control & GitHub</li>
                    <li>• Testing (JUnit, Mockito, Jest)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Program Structure */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">6-Month Program Structure</h2>
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-b from-orange-50 to-orange-100 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3 text-orange-700">Months 1-2: Foundation</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Core Java Mastery</li>
                    <li>• OOP Concepts</li>
                    <li>• Database Fundamentals</li>
                    <li>• HTML/CSS/JavaScript</li>
                  </ul>
                </div>
                <div className="bg-gradient-to-b from-blue-50 to-blue-100 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3 text-blue-700">Months 3-4: Advanced</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Spring Boot Development</li>
                    <li>• React.js Frontend</li>
                    <li>• API Development</li>
                    <li>• Microservices Basics</li>
                  </ul>
                </div>
                <div className="bg-gradient-to-b from-green-50 to-green-100 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3 text-green-700">Months 5-6: Expert</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Cloud Deployment</li>
                    <li>• DevOps Integration</li>
                    <li>• Capstone Project</li>
                    <li>• Interview Preparation</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Career Opportunities */}
        <div className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Career Opportunities</h2>
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4">Job Roles</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Full Stack Java Developer</li>
                    <li>• Backend Java Developer</li>
                    <li>• Spring Boot Developer</li>
                    <li>• Microservices Developer</li>
                    <li>• Java Software Engineer</li>
                    <li>• Technical Lead</li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h3 className="text-xl font-semibold mb-4">Salary Expectations</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Fresher: ₹4-8 LPA</li>
                    <li>• 1-2 Years: ₹8-15 LPA</li>
                    <li>• 3-5 Years: ₹15-25 LPA</li>
                    <li>• Senior: ₹25+ LPA</li>
                    <li>• Top Companies: Google, Amazon, Microsoft</li>
                    <li>• Startups: High growth potential</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enrollment Details */}
        <div className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Program Details</h2>
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="border-l-4 border-orange-500 pl-4">
                  <h3 className="font-semibold text-lg">Duration</h3>
                  <p className="text-gray-600">6 Months (Intensive Program)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="font-semibold text-lg">Mode</h3>
                  <p className="text-gray-600">Online Live Classes + Hands-on Labs</p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h3 className="font-semibold text-lg">Batch Size</h3>
                  <p className="text-gray-600">Limited to 25 students for personalized attention</p>
                </div>
              </div>
              <div className="space-y-6">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="font-semibold text-lg">Investment</h3>
                  <p className="text-gray-600">₹25,999 (EMI options available)</p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h3 className="font-semibold text-lg">Certification</h3>
                  <p className="text-gray-600">Industry-recognized certificate + Portfolio</p>
                </div>
                <div className="border-l-4 border-yellow-500 pl-4">
                  <h3 className="font-semibold text-lg">Support</h3>
                  <p className="text-gray-600">Lifetime career support & mentorship</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-16 bg-gradient-to-r from-orange-600 to-red-700 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Become a Full Stack Java Expert?</h2>
            <p className="text-xl mb-8">Join India's most comprehensive Java development program!</p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <button className="bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                Enroll Now - ₹25,999
              </button>
              <button className="border-2 border-white hover:bg-white hover:text-orange-600 px-8 py-4 rounded-lg font-semibold text-lg transition-colors">
                Schedule Free Demo
              </button>
            </div>
            <p className="mt-4 text-sm opacity-90">Next batch starts: 15th August 2024 | Limited seats available!</p>
          </div>
        </div>

        {/* Related Courses */}
        <div className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <h3 className="text-2xl font-bold text-center mb-8">Related Courses</h3>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/java-training-online" className="bg-orange-100 hover:bg-orange-200 px-4 py-2 rounded-lg text-orange-700 font-medium transition-colors">Core Java Training</a>
              <a href="/courses/spring-boot" className="bg-green-100 hover:bg-green-200 px-4 py-2 rounded-lg text-green-700 font-medium transition-colors">Spring Boot Course</a>
              <a href="/courses/react" className="bg-blue-100 hover:bg-blue-200 px-4 py-2 rounded-lg text-blue-700 font-medium transition-colors">React.js Training</a>
              <a href="/courses/aws" className="bg-yellow-100 hover:bg-yellow-200 px-4 py-2 rounded-lg text-yellow-700 font-medium transition-colors">AWS Certification</a>
              <a href="/courses/devops" className="bg-purple-100 hover:bg-purple-200 px-4 py-2 rounded-lg text-purple-700 font-medium transition-colors">DevOps Training</a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FullStackJava;
